import { Cancel01Icon } from "@hugeicons-pro/core-stroke-rounded";
import { useMutation } from "@tanstack/react-query";
import { instance } from "api/apiBase";
import { useEffect } from "react";
import { toast } from "react-toastify";
import {
  BaseInput,
  Button,
  ButtonSize,
  ButtonVariant,
  IconButton,
  IconButtonSize,
  IconButtonVariant,
  Modal
} from "shared/components";
import { Card } from "shared/components/Card";
import { numberParser } from "shared/components/forms/BaseInput/parsers";
import { useFormTyped } from "shared/hooks";
import { number, object } from "yup";

const validationSchema = object({
  desiredLeaseTerm: number()
    .typeError("This field is required")
    .required("This field is required")
    .min(2, "Desired lease term must be at least 2 months")
});

export const ChangeDesiredLeaseTermModal = ({
  opened,
  onClose,
  applicationId,
  desiredLeaseTerm,
  refetchApplication
}: {
  opened: boolean;
  onClose: () => void;
  applicationId: string;
  desiredLeaseTerm: number;
  refetchApplication: () => void;
}) => {
  const { register, onSubmit, reset } = useFormTyped(
    { desiredLeaseTerm: desiredLeaseTerm.toString() },
    validationSchema
  );

  const { mutate: updateDesiredLeaseTerm, isPending } = useMutation({
    mutationKey: ["UPDATE_DESIRED_LEASE_TERM"],
    mutationFn: (value: number) => {
      return instance.patch(`/portal/v1/applications/${applicationId}`, {
        desired_lease_term: value
      });
    },
    onSuccess: () => {
      refetchApplication();
      onClose();
    },
    onError: (error) => {
      toast.error(error.message || "Error");
    }
  });

  useEffect(() => {
    reset({ desiredLeaseTerm: desiredLeaseTerm.toString() });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [desiredLeaseTerm]);

  const handleSubmit = onSubmit({
    onSuccess: (values) => {
      updateDesiredLeaseTerm(Number(values.desiredLeaseTerm));
    }
  });

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      panelClassName="max-w-[328px] w-full rounded-xl"
    >
      <Card className="bg-background-1000 p-5 space-y-4">
        <div className="space-y-1">
          <div className="flex items-center justify-between">
            <h3 className="text-base font-bold text-content-1000">
              Edit desired lease term
            </h3>
            <IconButton
              variant={IconButtonVariant.TEXT_BASE}
              size={IconButtonSize.BASE}
              onClick={onClose}
              HugeIcon={Cancel01Icon}
            />
          </div>
          <div className="text-sm text-content-500">
            Desired lease term will be updated in the application details
          </div>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <BaseInput
            id="desired-lease-term"
            label="Desired lease term"
            required
            parser={numberParser({ asString: true })}
            {...register("desiredLeaseTerm")}
          />
          <div className="flex justify-end gap-2 bg-white">
            <Button
              size={ButtonSize.BASE}
              onClick={onClose}
              className="flex-1"
              type="button"
            >
              Cancel
            </Button>
            <Button
              size={ButtonSize.BASE}
              variant={ButtonVariant.PRIMARY}
              loading={isPending}
              className="flex-1"
              type="submit"
            >
              Save
            </Button>
          </div>
        </form>
      </Card>
    </Modal>
  );
};
